import React, {useState, useEffect} from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Grid,
  Alert,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  Assessment as ReportIcon,
  Add as AddIcon,
  FileDownload as FileDownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Api as ApiIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { formatDateToJerusalem } from '../../../utils/dateFormatter';
import jwtAxios from '@crema/services/auth/jwt-auth';

const DailyReportsListEnhanced = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filteredReports, setFilteredReports] = useState([]);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  useEffect(() => {
    fetchDailyReports();
  }, [pagination.page]);

  const fetchDailyReports = async () => {
    try {
      setLoading(true);
      const response = await jwtAxios.get('/daily-reports', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
        },
      });

      if (response.data.status === 200) {
        setReports(response.data.data || []);
        setFilteredReports(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.data?.length || 0,
        }));
      } else {
        setError(response.data.message || 'Failed to fetch daily reports');
      }
    } catch (err) {
      console.error('Error fetching daily reports:', err);
      setError('Failed to fetch daily reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event) => {
    const newDate = new Date(event.target.value);
    setSelectedDate(newDate);

    // Filter reports by date
    const filtered = reports.filter(
      (report) =>
        new Date(report.report_date).toDateString() === newDate.toDateString(),
    );
    setFilteredReports(filtered);
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setOpenReportDialog(true);
  };

  const handleDownloadReport = async (report) => {
    try {
      const response = await jwtAxios.get(
        `/daily-reports/${report.id}/download`,
        {
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        report.excel_file_name || `daily_report_${report.report_date}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading report:', err);
      setError('Failed to download report');
    }
  };

  const handleDownloadApiReport = async (reportId, apiId) => {
    try {
      const response = await jwtAxios.get(
        `/daily-reports/${reportId}/api/${apiId}/download`,
        {
          responseType: 'blob',
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `api_report_${apiId}_${reportId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading API report:', err);
      setError('Failed to download API report');
    }
  };

  const handleGenerateReport = async () => {
    try {
      setGeneratingReport(true);
      const reportDate = selectedDate.toISOString().split('T')[0];

      const response = await jwtAxios.post('/daily-reports/generate', {
        reportDate,
      });

      if (response.data.status === 200) {
        await fetchDailyReports();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleRefresh = () => {
    fetchDailyReports();
  };

  const handleDeleteReport = (report) => {
    setReportToDelete(report);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      const response = await jwtAxios.delete(`/daily-reports/${reportToDelete.id}`);
      
      if (response.data.status === 200) {
        // Refresh the reports list
        await fetchDailyReports();
        console.log('Report deleted successfully');
        setError(null);
      } else {
        console.error('Failed to delete report:', response.data.message);
        setError('Failed to delete report: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      setError('Error deleting report: ' + error.message);
    } finally {
      setDeleteDialogOpen(false);
      setReportToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setReportToDelete(null);
  };

  const handlePageChange = (event, page) => {
    setPagination((prev) => ({...prev, page}));
  };

  const handleDateExpand = (date) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Group reports by date
  const groupedReports = filteredReports.reduce((acc, report) => {
    const date = report.report_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(report);
    return acc;
  }, {});

  const SkeletonTable = () => (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Skeleton variant='text' width={200} height={40} />
        <Box display='flex' gap={2}>
          <Skeleton variant='rectangular' width={200} height={40} />
          <Skeleton variant='rectangular' width={100} height={40} />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {Array.from({length: 9}).map((_, index) => (
                <TableCell key={index}>
                  <Skeleton variant='text' width={100} height={24} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({length: 5}).map((_, index) => (
              <TableRow key={index}>
                {Array.from({length: 9}).map((_, cellIndex) => (
                  <TableCell key={cellIndex}>
                    <Skeleton variant='text' width={80} height={24} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  if (loading) {
    return (
      <Box>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          mb={3}
        >
          <Typography variant='h4'>Daily Reports</Typography>
        </Box>
        <SkeletonTable />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Typography variant='h4'>Daily Reports</Typography>
        <Box display='flex' gap={2}>
          <TextField
            type='date'
            label='Filter by Date'
            value={selectedDate.toISOString().split('T')[0]}
            onChange={handleDateChange}
            InputLabelProps={{shrink: true}}
            size='small'
          />
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleGenerateReport}
            disabled={generatingReport}
          >
            {generatingReport ? 'Generating...' : 'Generate Report'}
          </Button>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          {error && (
            <Alert severity='error' sx={{mb: 2}}>
              {error}
            </Alert>
          )}

          {Object.keys(groupedReports).length === 0 ? (
            <Box textAlign='center' py={4}>
              <Typography variant='h6' color='text.secondary'>
                No reports found
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{mt: 1}}>
                Generate a new report for the selected date
              </Typography>
            </Box>
          ) : (
            <Box>
              {Object.entries(groupedReports).map(([date, dateReports]) => (
                <Accordion
                  key={date}
                  expanded={expandedDates.has(date)}
                  onChange={() => handleDateExpand(date)}
                  sx={{mb: 2}}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display='flex' alignItems='center' width='100%'>
                      <CalendarIcon sx={{mr: 1, color: 'text.secondary'}} />
                      <Typography variant='h6' sx={{mr: 2}}>
                        {formatDateToJerusalem(date).split(' ')[0]}
                      </Typography>
                      <Chip
                        label={`${dateReports.length} Report${
                          dateReports.length > 1 ? 's' : ''
                        }`}
                        color='primary'
                        size='small'
                        sx={{mr: 2}}
                      />
                      <Box display='flex' gap={2} ml='auto'>
                        <Typography variant='body2' color='text.secondary'>
                          Total Leads:{' '}
                          {dateReports.reduce(
                            (sum, r) => sum + (r.total_leads || 0),
                            0,
                          )}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Valid:{' '}
                          {dateReports.reduce(
                            (sum, r) => sum + (r.valid_leads || 0),
                            0,
                          )}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Incomplete:{' '}
                          {dateReports.reduce(
                            (sum, r) => sum + (r.invalid_leads || 0),
                            0,
                          )}
                        </Typography>
                        <Typography variant='body2' color='warning.main' fontWeight='bold'>
                          Dup Free:{' '}
                          {dateReports.reduce(
                            (sum, r) => sum + (r.duplicated_free_data || 0),
                            0,
                          )}
                        </Typography>
                        <Typography variant='body2' color='error.main' fontWeight='bold'>
                          Dup Sent:{' '}
                          {dateReports.reduce(
                            (sum, r) => sum + (r.duplicated_sent_data || 0),
                            0,
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    {dateReports.map((report) => (
                      <Card key={report.id} sx={{mb: 2}}>
                        <CardContent>
                          <Box
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            mb={2}
                          >
                            <Typography variant='h6'>
                              Daily Report -{' '}
                              {formatDateToJerusalem(report.created_at)}
                            </Typography>
                            <Box display='flex' gap={1}>
                              <Tooltip title='View Details'>
                                <IconButton
                                  size='small'
                                  onClick={() => handleViewReport(report)}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='Download Combined Report'>
                                <IconButton
                                  size='small'
                                  onClick={() => handleDownloadReport(report)}
                                >
                                  <FileDownloadIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='Delete Report'>
                                <IconButton
                                  size='small'
                                  onClick={() => handleDeleteReport(report)}
                                  color='error'
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>

                          <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} sm={3}>
                              <Box textAlign='center'>
                                <Typography variant='h4' color='primary'>
                                  {report.total_leads}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Total Leads
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box textAlign='center'>
                                <Typography variant='h4' color='success.main'>
                                  {report.valid_leads}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Valid Leads
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box textAlign='center'>
                                <Typography variant='h4' color='error.main'>
                                  {report.invalid_leads}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Incomplete Leads
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box textAlign='center'>
                                <Typography variant='h4' color='warning.main'>
                                  {report.total_leads > 0
                                    ? (
                                        (report.valid_leads /
                                          report.total_leads) *
                                        100
                                      ).toFixed(1)
                                    : 0}
                                  %
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Success Rate
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box textAlign='center'>
                                <Typography variant='h4' color='warning.main'>
                                  {report.duplicated_free_data || 0}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Dup - Free
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <Box textAlign='center'>
                                <Typography variant='h4' color='error.main'>
                                  {report.duplicated_sent_data || 0}
                                </Typography>
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                >
                                  Dup - Sent
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          <Divider sx={{my: 2}} />

                          <Typography variant='subtitle1' gutterBottom>
                            API Reports
                          </Typography>

                          {report.api_details &&
                          report.api_details.length > 0 ? (
                            <List>
                              {report.api_details.map((apiDetail, index) => (
                                <ListItem
                                  key={index}
                                  divider={
                                    index < report.api_details.length - 1
                                  }
                                >
                                  <ListItemText
                                    primary={
                                      <Box display='flex' alignItems='center'>
                                        <ApiIcon
                                          sx={{mr: 1, color: 'text.secondary'}}
                                        />
                                        <Typography
                                          variant='body1'
                                          fontWeight='medium'
                                        >
                                          {apiDetail.api_name || 'Unknown API'}
                                        </Typography>
                                        <Chip
                                          label={apiDetail.leads_count || 0}
                                          color='primary'
                                          size='small'
                                          sx={{ml: 2}}
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <Box display='flex' gap={2} mt={1}>
                                        <Box display='flex' alignItems='center'>
                                          <CheckCircleIcon
                                            sx={{
                                              mr: 0.5,
                                              color: 'success.main',
                                              fontSize: 16,
                                            }}
                                          />
                                          <Typography
                                            variant='body2'
                                            color='success.main'
                                          >
                                            {apiDetail.valid_leads || 0} Valid
                                          </Typography>
                                        </Box>
                                        <Box display='flex' alignItems='center'>
                                          <ErrorIcon
                                            sx={{
                                              mr: 0.5,
                                              color: 'error.main',
                                              fontSize: 16,
                                            }}
                                          />
                                          <Typography
                                            variant='body2'
                                            color='error.main'
                                          >
                                            {apiDetail.invalid_leads || 0}{' '}
                                            Incomplete
                                          </Typography>
                                        </Box>
                                        <Typography
                                          variant='body2'
                                          color='text.secondary'
                                        >
                                          Success Rate:{' '}
                                          {apiDetail.leads_count > 0
                                            ? (
                                                (apiDetail.valid_leads /
                                                  apiDetail.leads_count) *
                                                100
                                              ).toFixed(1)
                                            : 0}
                                          %
                                        </Typography>
                                        <Typography
                                          variant='body2'
                                          color='warning.main'
                                          fontWeight='bold'
                                        >
                                          Dup Free: {apiDetail.duplicated_free_data || 0}
                                        </Typography>
                                        <Typography
                                          variant='body2'
                                          color='error.main'
                                          fontWeight='bold'
                                        >
                                          Dup Sent: {apiDetail.duplicated_sent_data || 0}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                  <ListItemSecondaryAction>
                                    <Tooltip title='Download API Report'>
                                      <IconButton
                                        size='small'
                                        onClick={() =>
                                          handleDownloadApiReport(
                                            report.id,
                                            apiDetail.api_credential_id,
                                          )
                                        }
                                      >
                                        <FileDownloadIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant='body2' color='text.secondary'>
                              No API details available
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {pagination.total > pagination.limit && (
            <Box display='flex' justifyContent='center' mt={3}>
              <Pagination
                count={Math.ceil(pagination.total / pagination.limit)}
                page={pagination.page}
                onChange={handlePageChange}
                color='primary'
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Report Details Dialog */}
      <Dialog
        open={openReportDialog}
        onClose={() => setOpenReportDialog(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center'>
            <ReportIcon sx={{mr: 1}} />
            Report Details - {selectedReport?.report_date}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Total Leads
                </Typography>
                <Typography variant='h6'>
                  {selectedReport.total_leads}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Valid Leads
                </Typography>
                <Typography variant='h6' color='success.main'>
                  {selectedReport.valid_leads}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Incomplete Leads
                </Typography>
                <Typography variant='h6' color='error.main'>
                  {selectedReport.invalid_leads}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  Success Rate
                </Typography>
                <Typography variant='h6'>
                  {selectedReport.total_leads > 0
                    ? (
                        (selectedReport.valid_leads /
                          selectedReport.total_leads) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  APIs Count
                </Typography>
                <Typography variant='h6'>
                  {selectedReport.api_count || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' color='text.secondary'>
                  File Size
                </Typography>
                <Typography variant='h6'>
                  {formatFileSize(selectedReport.file_size)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant='subtitle2'
                  color='text.secondary'
                  sx={{mb: 1}}
                >
                  API Names
                </Typography>
                <Typography variant='body2'>
                  {selectedReport.api_names || 'No APIs found'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportDialog(false)}>Close</Button>
          <Button
            variant='contained'
            startIcon={<FileDownloadIcon />}
            onClick={() => handleDownloadReport(selectedReport)}
          >
            Download Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box display='flex' alignItems='center'>
            <DeleteIcon sx={{mr: 1, color: 'error.main'}} />
            Delete Report
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{mb: 2}}>
            Are you sure you want to delete this report?
          </Typography>
          {reportToDelete && (
            <Box sx={{p: 2, bgcolor: 'grey.50', borderRadius: 1}}>
              <Typography variant='subtitle2' color='text.secondary'>
                Report Details:
              </Typography>
              <Typography variant='body2'>
                <strong>Name:</strong> {reportToDelete.name}
              </Typography>
              <Typography variant='body2'>
                <strong>Date:</strong> {new Date(reportToDelete.created_at).toLocaleString()}
              </Typography>
              <Typography variant='body2'>
                <strong>Total Leads:</strong> {reportToDelete.total_leads}
              </Typography>
            </Box>
          )}
          <Typography variant='body2' color='error.main' sx={{mt: 2}}>
            ⚠️ This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color='primary'>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color='error' 
            variant='contained'
            startIcon={<DeleteIcon />}
          >
            Delete Report
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyReportsListEnhanced;
